import React, { Suspense, useEffect, useState } from 'react';
import {
  Container,
  LoadingFull,
  PopUpFailedGenerateWorksheet,
  Snackbar,
} from 'geniebook-ui/dist';
import { useParams, useNavigate, useSearchParams } from 'react-router-dom';
import {
  useAbGroupStatus,
  useCheckNewAIComment,
  useSubjectMenuList,
} from '@hooks/student.hook';
import { convertToSlug } from '@utils/helpers/convertToSlug';
import { Helmet } from 'react-helmet-async';
import ErrorPage from '@pages/error/ErrorPage';
import FloatingButton from '@components/genieask/FloatingButton';
import WorksheetMinichat from '@pages/genieask/WorksheetMinichatPage';
import {
  TriggerBtnProps,
  WorksheetPopoverProps,
} from '@models/types/genieask.type';
import { useAtom, useStore } from 'jotai';
import { activeSubjectExpiredAtom } from '@atom/activeSubjectStore';
import {
  clickFloatingButtonAtom,
  resetDiscussQuestionAtom,
  showCompleteWorksheetAtom,
} from '@atom/worksheetAtom';
import MinichatFloatingButtonWrapper from '@components/genieask/MinichatFloatingButtonWrapper';
import WorksheetPopupCard from '@components/WorksheetPopupCard';
import {
  haveSearchButton,
  isSearching,
  NavbarState,
  resetToDefaultNavbarState,
  setNavbarWithTitleWithTopNavbar,
} from '@atom/navbarAtom';
import { useAtomValue } from 'jotai';
import { useJwt } from '@hooks/microservice/use-jwt';
import { useActiveListBanner } from '@hooks/microservice/cms.hook';
import { IActiveBannerListReq } from '@models/interfaces/microservice/cms.interface';
import { getCountryAbbrFromCountryID } from '@utils/helpers/country.helper';
import {
  IGenerateRevisionWorksheetRes,
  PreviewBanner,
} from '@models/interfaces/worksheet.interface';
import { IWorksheetCardProp } from '@models/interfaces/worksheet.interface';
import Class2SmartPopupHandler from '@components/student/class2smart/Class2SmartPopupHandler';
import GenerateWorksheetPopup from '@components/student/popup/GenerateWorksheetPopup';
import WorksheetServices from '@services/GeniebookAPI/WorksheetServices.service';
import {
  NEW_RECSYS_ERROR_LTA,
  INSUFFICIENT_WORKSHEET_ERROR_CODE,
  WORKSHEET_TYPES,
} from '@utils/constants/worksheet.constants';
import { useViewWidth } from '@utils/helpers/viewWidth';
import { subjectRouteFromId } from '@utils/helpers/SubjectParser';
import { BASE_URL, CDN_URL } from '@utils/constants/url.constants';
const breakpoint = require('geniebook-ui/dist/themes/breakpoint');
import { useQueryClient } from '@tanstack/react-query';
import { userDetailQueryAtom } from '@atom/globalQueryAtom';
import { useIsJCUser } from '@hooks/user.hook';
import { subjectIdFromName } from '@utils/helpers/subjectIdFromName';
import NewAICommentPopup from '@layouts/NewAICommentPopup';
import { AB_GROUP } from '@utils/constants/AbGroup.constants';

const WorksheetDashboard = React.lazy(
  () => import('geniesmart/WorksheetDashboard'),
);

type worksheetCatalogueOptions = {
  subject_id?: string | number | undefined;
  search?: string | undefined;
  page: string | number;
  limit: string | number;
  sort: string | undefined;
  status: 1 | 0;
  worksheet_id?: number; // to search ws specific by worksheet id
};

export const triggerBtn = (props: TriggerBtnProps) => {
  const { ready, unreadMessage } = props;
  return (
    <div>
      <FloatingButton ready={ready} unreadMessage={unreadMessage} />
    </div>
  );
};
export const fullChatGenieaskUrl = () => '/genieask';

const WorksheetDashboardPage = () => {
  useJwt();
  const [insufficientQuestionsPopup, setInsufficientQuestionsPopup] = useState<{
    show: boolean;
    subtopics: {
      id: number;
      name: string;
    }[];
  }>({
    show: false,
    subtopics: [],
  });
  const [activeListBannerProps, setActiveListBannerProps] =
    useState<IActiveBannerListReq>({
      markets: [],
      page: 0,
      page_size: 1,
      sort: '',
    });
  const activeListBannerQuery = useActiveListBanner(activeListBannerProps);

  const queryClient = useQueryClient();

  const { subjectId } = useParams();
  const { isJC } = useIsJCUser({
    subjectId: Number(subjectIdFromName(subjectId ?? '')),
  });
  const navigate = useNavigate();
  const [searchParam] = useSearchParams();
  const querySubjectMenuList = useSubjectMenuList();
  const subjectMenuList = querySubjectMenuList.data?.data?.data;
  const [subjectDbId, setSubjectDbId] = useState<number>();
  const [showCompleteWorksheet, setShowCompleteWorksheet] = useAtom(
    showCompleteWorksheetAtom,
  );
  const [previewBannerProps, setPreviewBannerProps] = useState<PreviewBanner>();
  const [loadingCreateWs, setLoadingCreateWs] = useState(false);
  const [error, setError] = useState({
    error: false,
    error_msg: '',
    error_code: '',
  });
  const [errorPopup, setErrorPopup] = useState({
    buttons: <></>,
    error: false,
    error_code: '',
    msg: '',
    on_close: () => {},
    title: '',
  });

  const [showNewAIComment, setShowNewAIComment] = useState<boolean>(false);
  const [urlParams, setUrlParams] = useSearchParams();

  const param = useParams<{ subjectId: string }>();

  const checkAbGroupStatus = useAbGroupStatus({
    subject_id: subjectIdFromName(param.subjectId || ''),
    ab_group_type: AB_GROUP.aiComment,
  });

  const [query, setQuery] = useState<worksheetCatalogueOptions>({
    search: '',
    page: 1,
    limit: 16,
    sort: 'completion',
    status: 1,
  });

  const checkNewAIComment = useCheckNewAIComment({
    subject_id: subjectIdFromName(param.subjectId || ''),
  });

  const updateLocalStorageWorksheetTab = (status: 1 | 0) => {
    localStorage.setItem('currentWorksheetTab', String(status));
  };

  useEffect(() => {
    if (checkAbGroupStatus.data?.is_ab_group) {
      setShowNewAIComment(
        checkNewAIComment.data?.data.data.has_new_comment ?? false,
      );
    }
  }, [checkAbGroupStatus]);

  const updateQueryStatus = (status: 0 | 1) => {
    setQuery({ ...query, page: 1, status: status });
    setUrlParams('');
    updateLocalStorageWorksheetTab(status);
  };

  useEffect(() => {
    if (urlParams.get('completed_tab')) {
      setTimeout(() => {
        updateQueryStatus(0);
      }, 500);
    }
  }, [urlParams]);

  const clearError = () => {
    setError({
      error: false,
      error_code: '',
      error_msg: '',
    });

    setErrorPopup({
      ...errorPopup,
      buttons: <></>,
      error: false,
      error_code: '',
      msg: '',
      on_close: () => {},
      title: '',
    });
  };
  const contactSupport = () => {
    window.open('https://geniebook.com/contact', '_blank');
    clearError();
  };
  useEffect(() => {
    if (error.error) setTimeout(clearError, 5000);
  }, [error]);
  const [generateWorksheetPopupProp, setGenerateWorksheetPopupProp] = useState<
    IWorksheetCardProp & { isOpen: boolean }
  >({
    isOpen: false,
    worksheet_id: '',
    worksheet_type: '',
    topic: '',
    is_recommended_worksheet: 0,
    lesson_plan_topics: [],
    lesson_plan_knowledges: [],
    teacher_id: '',
    worksheet_name: '',
    time_limit: '',
    completed: '0',
  });

  async function createWorksheet(worksheet: IWorksheetCardProp) {
    try {
      setLoadingCreateWs(true);
      const createWs = await WorksheetServices.generateRevisionWs({
        subject_id: parseInt(worksheet.subject_id || ''),
        topic_ids: worksheet.topic_ids,
        skill_decay: true,
        notification_id_skill_decay: worksheet.notification_id,
      });
      if (createWs.data.error) {
        if (createWs.data.error_code === INSUFFICIENT_WORKSHEET_ERROR_CODE) {
          // Insufficient questions from new recsys
          // show popup
          setInsufficientQuestionsPopup({
            show: true,
            subtopics: worksheet.worksheet_name
              .split(',')
              ?.map((data, index) => ({
                id: index,
                name: data.trim(),
              })),
          });
          return;
        }
        setError({
          ...error,
          error: true,
          error_code: createWs.data.error_code,
          error_msg:
            'Something went wrong, please wait a few minutes and try again.',
        });
        return;
      }

      const openWorksheet = () => {
        window.open(
          `${BASE_URL}${subjectRouteFromId(
            parseInt(worksheet.subject_id || ''),
          )}/worksheets/worksheet_question/${createWs.data.data.worksheet_id}`,
          '_blank',
          'noopener,noreferrer',
        );
      };

      setTimeout(() => {
        openWorksheet();
        location.reload();
      });
    } catch (e) {
      setError({
        ...error,
        error: true,
        error_code: '',
        error_msg:
          'Something went wrong, please wait a few minutes and try again.',
      });
    } finally {
      setLoadingCreateWs(false);
    }
  }

  async function createRevisionWorksheet(worksheet: IWorksheetCardProp) {
    try {
      setLoadingCreateWs(true);
      const res = await WorksheetServices.generateRevisionWs({
        subject_id: parseInt(worksheet.subject_id || ''),
        personalised_revision_id: worksheet?.personalised_revision_id,
      });
      const body: IGenerateRevisionWorksheetRes = res.data;

      // redirect to view worksheet page on success
      if (!body.error && body.data?.worksheet_id) {
        const subjectRoute = subjectRouteFromId(
          parseInt(worksheet.subject_id || ''),
        );

        const openWorksheet = () => {
          window.open(
            `${BASE_URL}${subjectRoute}/worksheets/worksheet_question/${body.data.worksheet_id}`,
            '_blank',
            'noopener,noreferrer',
          );
        };

        setTimeout(() => {
          openWorksheet();
          location.reload();
        });
        return;
      }

      if (body.error_code == '099-08') {
        // Error while trying to tag generate_genie_ws or generate_revision_ws
        setError({
          ...error,
          error: true,
          error_code: body.error_code,
          error_msg:
            'Something went wrong. Please refresh the page and try again.',
        });
      } else if (body.error_code == '300-02') {
        // Error invalid parameters
        setError({
          ...error,
          error: true,
          error_code: body.error_code,
          error_msg: 'Something went wrong, please contact us for assistance.',
        });
      } else if (body.error_code == '099-09') {
        // Error while trying to create generate_genie_ws or generate_revision_ws because already created auto generated worksheet for this week
      } else if (body.error_code == NEW_RECSYS_ERROR_LTA) {
        setError({
          ...error,
          error: true,
          error_code: body.error_code,
          error_msg: body.error_msg,
        });
      } else {
        setError({
          ...error,
          error: true,
          error_code: body.error_code,
          error_msg:
            'Something went wrong. Please wait a few minutes and try again.',
        });
      }
    } catch (e) {
      console.error('Error Generating Revision Worksheet ', e);
      setError({
        ...error,
        error: true,
        error_code: '',
        error_msg:
          'Something went wrong, please wait a few minutes and try again.',
      });
    } finally {
      setLoadingCreateWs(false);
    }
  }

  const viewWidth = useViewWidth();

  const store = useStore();
  const navbarState = useAtomValue(NavbarState);

  useEffect(() => {
    if (showCompleteWorksheet) {
      // setNavbarWithTitleWithTopNavbar('Completed/Expired');
    } else {
      resetToDefaultNavbarState();
      store.set(haveSearchButton, true);
    }
  }, [showCompleteWorksheet]);

  useEffect(() => {
    store.set(haveSearchButton, true);
    return () => {
      resetToDefaultNavbarState();
    };
  }, []);

  useEffect(() => {
    if (querySubjectMenuList.isLoading) return;
    if (!subjectMenuList || subjectMenuList.length < 1) {
      if (subjectId === undefined)
        navigate({
          pathname: '/mathematics/worksheets',
          search: searchParam.toString(),
        }); // redirect to worksheet list Math if there is no given subject
      return;
    }

    const defaultUrl: string =
      '/' + convertToSlug(subjectMenuList[0].display_name) + '/worksheets'; // set default subject to the first given
    if (subjectId === undefined) {
      navigate({ pathname: defaultUrl, search: searchParam.toString() }); // redirect to default subject on empty subjectCode
    }
    // get subjectId from given subjectCode
    let selectedSubjectId: number = 0;
    for (let i = 0; i < subjectMenuList.length; i++) {
      if (convertToSlug(subjectMenuList[i].display_name) == subjectId) {
        selectedSubjectId = parseInt(subjectMenuList[i].subject_id);
        break;
      }
    }

    if (selectedSubjectId == 0)
      navigate({ pathname: defaultUrl, search: searchParam.toString() }); // redirect to default subject on invalid subjectCode

    setSubjectDbId(selectedSubjectId);
  }, [subjectMenuList, subjectId]);

  const defaultSkillDecayPopupDescription =
    "Dive back into [topic_names] that you've not practised in awhile. 1 credit will be used to create this worksheet.";
  const [skillDecayPopupDescription, setSkillDecayPopupDescription] =
    useState<React.ReactNode>(<></>);
  const [revisionPopupDescription, setRevisionPopupDescription] =
    useState<React.ReactNode>(<></>);

  const generateSkillDecayPopupDescription = (
    templateDescription: string,
    topicName: string,
  ) => {
    const maxLength = 150;
    const currentLength = templateDescription.length;

    const potentialNewDescription = templateDescription.replace(
      /\[topic_names\]/,
      topicName,
    ); // replace tag topic_names with real topic name

    if (potentialNewDescription.length > maxLength) {
      let truncatedTopicName =
        topicName.substring(0, maxLength - currentLength - 4) + '...'; // 4 for "..."
      // Additional check to ensure final description is under 150 characters
      while (
        templateDescription.replace(/\[topic_names\]/, truncatedTopicName)
          .length > maxLength
      )
        truncatedTopicName = truncatedTopicName.substring(
          0,
          truncatedTopicName.length - 1,
        );

      setSkillDecayPopupDescription(
        <>
          Dive back into <b>{truncatedTopicName}</b> that you've not practised
          in awhile.
          <br />1 credit will be used to create this worksheet.
        </>,
      );
    } else
      setSkillDecayPopupDescription(
        <>
          Dive back into <b>{topicName}</b> that you've not practised in awhile.
          <br />1 credit will be used to create this worksheet.
        </>,
      );
  };

  const generateRevisionPopupDescription = (worksheet: IWorksheetCardProp) => {
    const startDate = worksheet.start_date;
    const moment = require('moment');

    const momentStartDate = new moment(startDate).subtract(14, 'days');
    const momentDueDate = new moment(startDate).subtract(1, 'day');

    momentStartDate.format('D MMM');
    momentDueDate.format('D MMM');

    setRevisionPopupDescription(
      <>
        Review and correct mistakes made between{' '}
        <b>
          {momentStartDate.format('D MMM')} to {momentDueDate.format('D MMM')}
        </b>{' '}
        with this revision worksheet.
        <br />1 credit will be used to create this worksheet.
      </>,
    );
  };

  // keeps the Mock Exam data of the currently clicked Worksheet in `localStorage`
  const setStorageMockExamDetail = (worksheet: IWorksheetCardProp) => {
    // clears the storage if `is_mock_exam_worksheet` is not '1' and storage is set
    if (
      worksheet?.is_mock_exam_worksheet !== '1' &&
      localStorage.getItem('mockExamDetail')
    ) {
      localStorage.removeItem('mockExamDetail'); // used for one specific Mock Exam Worksheet
    }

    // set storage for `is_mock_exam_worksheet`
    if (
      worksheet?.is_mock_exam_worksheet ||
      worksheet.worksheet_type === WORKSHEET_TYPES.ASSESSMENT
    ) {
      localStorage.setItem(
        'mockExamDetail',
        worksheet.mock_exam_detail
          ? JSON.stringify(worksheet.mock_exam_detail)
          : '',
      );
    }
  };

  const navigateQuestionPage = (worksheet: IWorksheetCardProp) => {
    if (parseInt(worksheet.completed) > 0) {
      setNavbarWithTitleWithTopNavbar(worksheet.worksheet_name);
      return;
    }

    // normal worksheet
    if (worksheet.is_recommended_worksheet != 1) {
      setStorageMockExamDetail(worksheet);
      navigate(
        `/${subjectId}/worksheets/worksheet_question/${worksheet.worksheet_id}`,
      );
    }
    // recommended worksheet
    else if (worksheet.is_recommended_worksheet == 1)
      setGenerateWorksheetPopupProp({
        ...worksheet,
        isOpen: true,
      });

    if (
      worksheet.is_recommended_worksheet === 1 &&
      worksheet.worksheet_type === WORKSHEET_TYPES.SKILL_DECAYS
    )
      generateSkillDecayPopupDescription(
        defaultSkillDecayPopupDescription,
        worksheet.worksheet_name,
      );
    else if (
      worksheet.is_recommended_worksheet == 1 &&
      worksheet.worksheet_type === WORKSHEET_TYPES.REVISION
    )
      generateRevisionPopupDescription(worksheet);
  };

  const navigateCreateWorksheetPage = () => {
    navigate({ pathname: `/${subjectId}/worksheets/create` });
  };

  // Discuss Question feature
  const [resetDiscussQuestionProps, setResetDiscussQuestionProps] = useAtom(
    resetDiscussQuestionAtom,
  );

  const [discussQuestionProps, setDiscussQuestionProps] =
    React.useState<WorksheetPopoverProps>();

  // will be triggered when clicking on the Teacher Chat button on Worksheet Dashboard page, because it's changing the `resetDiscussQuestionProps` state
  useEffect(() => {
    if (resetDiscussQuestionProps) {
      setDiscussQuestionProps(undefined);
    }
    return () => {
      setResetDiscussQuestionProps(false);
    };
  }, [resetDiscussQuestionProps]);

  const onDiscussQuestionClick = (param: WorksheetPopoverProps) => {
    setDiscussQuestionProps(param);
    setClickFloatingButton(!clickFloatingButton);
  };

  const handleShowCompleteWorksheet = (show: boolean) => {
    setShowCompleteWorksheet(show);
  };
  const [activeSubjectExpired] = useAtom(activeSubjectExpiredAtom);

  const [clickFloatingButton, setClickFloatingButton] = useAtom(
    clickFloatingButtonAtom,
  );

  const userDetail = useAtomValue(userDetailQueryAtom);

  useEffect(() => {
    if (userDetail.isSuccess && !userDetail.data.error) {
      const country_id = userDetail.data.data.country_id;
      setActiveListBannerProps(props => ({
        ...props,
        markets: [getCountryAbbrFromCountryID(parseInt(country_id))],
      }));
    }
  }, [userDetail.data?.data]);

  useEffect(() => {
    if (
      activeListBannerQuery.isSuccess &&
      activeListBannerQuery.data.items.length > 0
    ) {
      const res = activeListBannerQuery.data.items[0];
      const data: PreviewBanner = {
        id: res.id,
      };

      // web
      if (res.content.desktop_image) {
        data.web = {
          action: res.content.click_url,
          image: res.content.desktop_image,
        };
      }

      // tablet
      if (res.content.tablet_image) {
        data.tablet = {
          action: res.content.click_url,
          image: res.content.tablet_image,
        };
      }

      // mobile
      if (res.content.mobile_image) {
        data.mobile = {
          action: res.content.click_url,
          image: res.content.mobile_image,
        };
      }

      // set the name
      data.name = res.name;
      setPreviewBannerProps(data);
    }
  }, [activeListBannerQuery.data?.items]);

  const hide_ai_comment_popup = () => {
    setShowNewAIComment(false);
  };

  return (
    <>
      <Helmet>
        <title>Geniebook - Worksheet</title>
        <meta name="description" content="Worksheet" />

        <style>{`
          body {
            background: #16143e!important;
          }

          .sticky + .grid {
            color: #fff;
          }
        `}</style>
      </Helmet>
      <Suspense fallback={<>Loading...</>}>
        {querySubjectMenuList.isLoading ? (
          // On Loading
          ''
        ) : // On Success
        querySubjectMenuList.isSuccess &&
          querySubjectMenuList.data.data.error === false &&
          subjectDbId ? (
          <Container className="relative">
            {activeSubjectExpired === null ? (
              <></>
            ) : (
              <>
                {showNewAIComment &&
                !localStorage.getItem('has_shown_new_ai_comment') ? (
                  <NewAICommentPopup
                    hide_ai_comment_popup={hide_ai_comment_popup}
                    subject_name={subjectId || ''}
                  />
                ) : (
                  ''
                )}
                <WorksheetDashboard
                  onCardClick={navigateQuestionPage}
                  subjectId={subjectDbId ? subjectDbId : 0}
                  onCreateWorksheet={navigateCreateWorksheetPage}
                  onViewCompletedWorksheet={handleShowCompleteWorksheet}
                  viewCompletedWorksheet={showCompleteWorksheet}
                  //! pending feature (onDiscussQuestionClick)
                  onDiscussQuestionClick={onDiscussQuestionClick}
                  activeSubjectExpired={activeSubjectExpired}
                  isSearching={navbarState.isSearching}
                  onSearch={keyword => store.set(isSearching, !!keyword)}
                  // preview banner
                  previewBanner={previewBannerProps}
                />
                <WorksheetPopupCard />
                {generateWorksheetPopupProp.worksheet_type ==
                  WORKSHEET_TYPES.HOMEWORK && (
                  <Class2SmartPopupHandler
                    show={generateWorksheetPopupProp.isOpen}
                    data={generateWorksheetPopupProp}
                    onClose={() => {
                      setGenerateWorksheetPopupProp(props => {
                        return {
                          ...props,
                          isOpen: false,
                        };
                      });
                    }}
                  />
                )}
                {generateWorksheetPopupProp.isOpen &&
                  generateWorksheetPopupProp.worksheet_type ==
                    WORKSHEET_TYPES.SKILL_DECAYS && (
                    <GenerateWorksheetPopup
                      title={"It's Recap O' Clock!"}
                      description={skillDecayPopupDescription}
                      onCreateWorksheet={() => {
                        createWorksheet(generateWorksheetPopupProp);
                        setGenerateWorksheetPopupProp(props => {
                          return {
                            ...props,
                            isOpen: false,
                          };
                        });
                      }}
                      onClosePopup={() => {
                        setGenerateWorksheetPopupProp(props => {
                          return {
                            ...props,
                            isOpen: false,
                          };
                        });
                      }}
                    />
                  )}
                {generateWorksheetPopupProp.isOpen &&
                  generateWorksheetPopupProp.worksheet_type ==
                    WORKSHEET_TYPES.REVISION && (
                    <GenerateWorksheetPopup
                      title={'Conquer your mistakes'}
                      description={revisionPopupDescription}
                      onCreateWorksheet={() => {
                        createRevisionWorksheet(generateWorksheetPopupProp);
                        setGenerateWorksheetPopupProp(props => {
                          return {
                            ...props,
                            isOpen: false,
                          };
                        });
                      }}
                      onClosePopup={() => {
                        setGenerateWorksheetPopupProp(props => {
                          return {
                            ...props,
                            isOpen: false,
                          };
                        });
                      }}
                    />
                  )}
              </>
            )}
            {/* the following Floating Button should opens a new tab for Genieask fullpage */}
            {/* <FloatingButton /> */}
            {error.error ? (
              <Snackbar
                message={error.error_msg}
                views={viewWidth < breakpoint.md ? 'mobile' : 'desktop'}
                buttonText={
                  error.error_code == '300-02' ||
                  error.error_code == NEW_RECSYS_ERROR_LTA
                    ? 'Contact Us'
                    : 'Dismiss'
                }
                onTextClick={
                  error.error_code == '300-02' ||
                  error.error_code == NEW_RECSYS_ERROR_LTA
                    ? contactSupport
                    : clearError
                }
                states={'error'}
                className={'fixed center-horizontal top-[84px]'}
              />
            ) : null}

            {loadingCreateWs ? (
              <LoadingFull disableDecorations={Boolean(isJC)}>
                <div
                  className={`text-center text-white ${
                    isJC ? 'flex flex-col items-center' : ''
                  }`}
                >
                  {Boolean(isJC) && (
                    <img
                      src={`${CDN_URL}assets/animations/GB-loading-minimalistic.gif`}
                      className="mb-5"
                      alt="loading spinner"
                      width={25}
                      height={25}
                    />
                  )}
                  <p className={'mb-5 font-black text-xl leading-6'}>
                    Creating Worksheet…
                  </p>
                  <p className={'mb-0'}>
                    Take your time to learn and understand the concepts. You can
                    do it!
                  </p>
                </div>
              </LoadingFull>
            ) : null}

            {/* the following Floating Button will open a Genieask minichat */}
            <MinichatFloatingButtonWrapper>
              <WorksheetMinichat
                trigger={triggerBtn}
                initialScreen={
                  showCompleteWorksheet ? 'worksheet' : 'mini-chat'
                }
                fullChatUrl={fullChatGenieaskUrl()}
                {...(discussQuestionProps
                  ? { worksheet: discussQuestionProps }
                  : {})}
              />
            </MinichatFloatingButtonWrapper>
          </Container>
        ) : // When student created from app/school/[school_code] but not assigned to any worksheet yet
        userDetail?.data?.data &&
          !Number(userDetail.data.data.has_lead) &&
          !Number(userDetail.data.data.has_parent) &&
          querySubjectMenuList.isSuccess &&
          querySubjectMenuList.data.data.data.length == 0 ? (
          <ErrorPage code="no-student-data" showNavBar={false} /> // On Success but no subject received
        ) : querySubjectMenuList.isSuccess &&
          querySubjectMenuList.data.data.error === false &&
          subjectMenuList !== undefined &&
          subjectMenuList.length === 0 ? (
          <ErrorPage code="no-subscription" />
        ) : // On Success but error specified
        querySubjectMenuList.isSuccess &&
          querySubjectMenuList.data.data.error === true ? (
          <ErrorPage code="500" showNavBar={false} />
        ) : // On Error Requests
        querySubjectMenuList.isError ? (
          <ErrorPage code="500" showNavBar={false} />
        ) : (
          <></>
        )}

        <PopUpFailedGenerateWorksheet
          show={insufficientQuestionsPopup.show}
          subtopics={insufficientQuestionsPopup.subtopics}
          className="!max-w-[calc(100%-32px)]"
          onConfirm={() => {
            setInsufficientQuestionsPopup({
              subtopics: [],
              show: false,
            });
            navigate(`/${subjectId}/worksheets/create`);
          }}
          onClose={() => {
            // refresh ws list
            queryClient.refetchQueries(['worksheets']);

            // close popup
            setInsufficientQuestionsPopup({
              subtopics: [],
              show: false,
            });
          }}
        />
      </Suspense>
    </>
  );
};

export default WorksheetDashboardPage;
